import { Button, HeaderText } from "@/components/ui";
import { Tooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import { Trash2 } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";

interface EditableRecordButtonProps {
  title?: string;
  onClick: () => void;
  children: ReactNode;
  containerClassName?: string;
  disabled?: boolean;
  onDelete?: () => void;
  deletePending?: boolean;
  deleteTooltipText?: string;
}

export function EditableRecordButton({
  title,
  onClick,
  children,
  containerClassName,
  disabled = false,
  onDelete,
  deletePending,
  deleteTooltipText = "Delete",
}: EditableRecordButtonProps) {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) {
      return;
    }

    const isActivationKey = e.key === "Enter" || e.key === " ";
    if (!isActivationKey) {
      return;
    }

    e.preventDefault();
    if (disabled) {
      return;
    }

    onClick();
  };

  return (
    <Tooltip tooltipContentText={disabled ? "Disabled" : "Edit"} side="bottom">
      <div className="group">
        <div
          onClick={() => !disabled && onClick()}
          onKeyDown={handleKeyDown}
          // biome-ignore lint/a11y/useSemanticElements: <explanation>
          role="button"
          tabIndex={disabled ? -1 : 0}
          className={cn(
            "flex relative flex-col gap-2 transition-colors px-3 py-2 w-full text-left",
            disabled
              ? "cursor-not-allowed [&_*]:cursor-not-allowed"
              : "cursor-pointer [&_*]:cursor-pointer group-hover:bg-tertiary",
          )}
          aria-label={`Edit ${title?.toLowerCase() || "record"}`}
          aria-disabled={disabled || undefined}
        >
          {title && <HeaderText size="sm">{title}</HeaderText>}
          <div
            className={cn(
              "flex flex-col gap-2 duration-300 relative",
              disabled && "cursor-not-allowed text-neutral-500",
              containerClassName,
              onDelete && !disabled && "[&>*:first-child]:pr-5",
            )}
          >
            {children}
            {onDelete && !disabled && (
              <div className="absolute right-0 group-hover:opacity-100 opacity-0 transition-opacity">
                <Tooltip side="right" tooltipContentText={deleteTooltipText}>
                  <Button
                    variant="icon"
                    className="m-0 p-0 flex"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.();
                    }}
                    isLoading={deletePending}
                    data-testid="delete-button"
                  >
                    <Trash2 className="stroke-link h-4 w-4" size={12} />
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </div>
    </Tooltip>
  );
}
